// Fase 7A — Calendario receptionist (CROSS-TENANT).
// Mostra i richiami della receptionist autenticata su TUTTI i suoi saloni.
// Indipendente dal TenantSwitcher globale (ha un proprio filtro opzionale).
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, CalendarDays } from "lucide-react";
import { api, createAgencyClient } from "@/lib/api";
import { useAgencyWorkspace } from "@/context/AgencyWorkspaceContext";
import { callbackTone, fmtTime, CB_LEGEND } from "@/lib/callbacks";
import CallbackDetail from "@/pages/admin/agency/CallbackDetail";
import CallbackModal from "@/components/CallbackModal";
import AgencyContactDrawerHost from "@/pages/admin/agency/AgencyContactDrawerHost";

function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function mondayOf(d) {
  const x = new Date(d); const dow = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x;
}
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function AgencyCalendarPage() {
  const { availableTenants } = useAgencyWorkspace();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [rows, setRows] = useState([]);
  const [salon, setSalon] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [contact, setContact] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  }), [weekStart]);

  const load = useCallback(async () => {
    const from = ymd(days[0]); const to = ymd(days[6]);
    try {
      const params = new URLSearchParams({ date_from: from, date_to: to });
      if (salon !== "all") params.set("tenant_id", salon);
      const r = await api.get(`/agency/callbacks?${params.toString()}`);
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [days, salon]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[ymd(d)] = []; });
    rows.forEach((cb) => {
      const key = ymd(new Date(cb.scheduled_at));
      if (m[key]) m[key].push(cb);
    });
    Object.values(m).forEach((list) => list.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    return m;
  }, [rows, days]);

  const todayKey = ymd(new Date());
  const shiftWeek = (delta) => { const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7); setWeekStart(d); };

  const rangeLabel = `${days[0].toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}`;

  return (
    <div data-testid="aw-calendar-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="font-display text-2xl font-black flex items-center gap-2 mr-2"><CalendarDays className="h-6 w-6 text-[color:var(--sp-brand)]" /> Calendario</h1>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftWeek(-1)} data-testid="cal-prev-week" className="h-8 w-8 rounded-lg border border-[color:var(--sp-border)] flex items-center justify-center hover:bg-[color:var(--sp-subtle)]"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="cal-today" className="text-xs font-bold px-3 h-8 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">Oggi</button>
          <button onClick={() => shiftWeek(1)} data-testid="cal-next-week" className="h-8 w-8 rounded-lg border border-[color:var(--sp-border)] flex items-center justify-center hover:bg-[color:var(--sp-subtle)]"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <span className="text-sm font-bold">{rangeLabel}</span>
        <select data-testid="cal-salon-filter" className="sp-input !w-auto !py-1.5 text-sm ml-auto" value={salon} onChange={(e) => setSalon(e.target.value)}>
          <option value="all">Tutti i miei saloni</option>
          {availableTenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button onClick={load} className="sp-btn-ghost inline-flex items-center gap-1 text-sm"><RefreshCw className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="cal-week-grid">
        {days.map((d, i) => {
          const key = ymd(d);
          const isToday = key === todayKey;
          const list = byDay[key] || [];
          return (
            <div key={key} className={`sp-card p-2 min-h-[180px] ${isToday ? "ring-2 ring-[color:var(--sp-brand)]" : ""}`}>
              <div className="text-center mb-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{DOW[i]}</div>
                <div className={`text-lg font-black ${isToday ? "text-[color:var(--sp-brand)]" : ""}`}>{d.getDate()}</div>
              </div>
              <div className="space-y-1.5">
                {list.length === 0 ? (
                  <div className="text-[10px] text-center text-[color:var(--sp-text-muted)] italic py-2">—</div>
                ) : list.map((cb) => {
                  const tone = callbackTone(cb);
                  return (
                    <button key={cb.id} data-testid={`cal-event-${cb.id}`} onClick={() => setDetail(cb)}
                      className={`w-full text-left rounded-lg border px-2 py-1.5 text-xs ${tone.cls} hover:opacity-80`}>
                      <div className="font-black">{fmtTime(cb.scheduled_at)}</div>
                      <div className="font-bold truncate">{cb.client?.name || "Contatto"}</div>
                      <div className="truncate opacity-80">{cb.tenant?.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {loading && <div className="text-center text-sm text-[color:var(--sp-text-muted)] mt-4">Carico…</div>}

      <div className="flex flex-wrap gap-2 mt-4 text-[10px]" data-testid="cb-legend">
        {CB_LEGEND.map((l) => <span key={l.label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold ${l.cls}`}>{l.label}</span>)}
      </div>

      {detail && (
        <CallbackDetail callback={detail} tenantClient={createAgencyClient(() => detail.tenant?.id)} onClose={() => setDetail(null)} onChanged={load}
          onOpenContact={(cb) => { setDetail(null); setContact(cb); }}
          onScheduleFollowUp={(cb) => setFollowUp(cb)} />
      )}
      {contact && (
        <AgencyContactDrawerHost clientId={contact.client_id} tenant={contact.tenant}
          caseObj={contact.case ? { id: contact.agency_case_id, ...contact.case } : null}
          onClose={() => setContact(null)} onUpdated={load} />
      )}
      {followUp && (
        <CallbackModal tenant={followUp.tenant} clientId={followUp.client_id} clientName={followUp.client?.name}
          caseObj={followUp.agency_case_id ? { id: followUp.agency_case_id } : null}
          onClose={() => setFollowUp(null)} onSaved={load} />
      )}
    </div>
  );
}
